import React from "react";
import { Link, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { getImportRun } from "../api/adminApi";
import { StatusBadge, ShortId, fmtDate, Section } from "../components/admin/AdminUI";

const COUNT_KEYS = [
  "received", "raw_stored", "mapped_success", "mapped_failed",
  "duplicates", "listings_created", "listings_updated", "listings_inactive",
  "skipped_no_url", "skipped_no_price", "skipped_invalid_category",
  "price_changes_detected", "active_total_after_run",
  "image_health_alive", "image_health_dead", "image_health_unchecked",
];

const STACK_KEYS = [
  { key: "mapped_success", color: "bg-emerald-500/70", label: "OK" },
  { key: "mapped_failed", color: "bg-rose-500/70", label: "Fail" },
  { key: "duplicates", color: "bg-zinc-500/70", label: "Dupes" },
  { key: "skipped_no_url", color: "bg-amber-500/40", label: "no_url" },
  { key: "skipped_no_price", color: "bg-amber-500/30", label: "no_price" },
  { key: "skipped_invalid_category", color: "bg-amber-500/20", label: "invalid_cat" },
];

export default function AdminImportDetailPage() {
  const { ulid } = useParams();
  const { data, isLoading, isError, error } = useQuery({
    queryKey: ["admin", "import-run", ulid],
    queryFn: () => getImportRun(ulid),
    enabled: Boolean(ulid),
  });

  if (isLoading) return <div className="text-zinc-500">Loading import run…</div>;
  if (isError)
    return (
      <div data-testid="import-detail-error" className="text-rose-400">
        {error?.response?.status === 404
          ? "Import run not found."
          : String(error?.message || error)}
        <div className="mt-4">
          <Link to="/admin/imports" className="text-emerald-300 underline">
            ← back to imports
          </Link>
        </div>
      </div>
    );
  if (!data) return null;

  const counts = data.counts || {};
  const total = STACK_KEYS.reduce((s, k) => s + (counts[k.key] || 0), 0) || 1;

  return (
    <div data-testid="admin-import-detail-page" className="space-y-8">
      <div className="flex items-center gap-3 flex-wrap">
        <Link to="/admin/imports" className="text-sm text-zinc-400 hover:text-zinc-200">
          ← Back to imports
        </Link>
      </div>

      <header className="space-y-2">
        <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-emerald-400">
          import_run
        </span>
        <h1 className="text-2xl text-zinc-100 font-medium font-mono break-all">
          {data.ulid}
        </h1>
        <div className="text-xs text-zinc-500 flex flex-wrap items-center gap-2">
          <span>{fmtDate(data.started_at)}</span>
          <span>→</span>
          <span>{fmtDate(data.finished_at)}</span>
          <span>·</span>
          <StatusBadge status={data.status} />
          <span>·</span>
          <span>{data.source_platform}</span>
          <span>·</span>
          <span>{data.vertical}</span>
          <span>·</span>
          <span>{data.duration_ms} ms</span>
          <span>·</span>
          <span>trigger: {data.trigger}</span>
        </div>
      </header>

      {/* 16-count grid */}
      <Section title="Counts">
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2" data-testid="import-counts-grid">
          {COUNT_KEYS.map((k) => (
            <div
              key={k}
              data-testid={`import-count-${k}`}
              className="rounded-lg border border-zinc-800 bg-zinc-950/50 px-3 py-2"
            >
              <div className="text-[10px] uppercase tracking-widest text-zinc-500">{k}</div>
              <div className="text-lg text-zinc-100 tabular-nums">
                {counts[k] ?? "—"}
              </div>
            </div>
          ))}
        </div>
      </Section>

      {/* Stacked bar */}
      <Section title="Pipeline leakage">
        <div className="flex h-3 rounded-md overflow-hidden border border-zinc-800">
          {STACK_KEYS.map((s) => {
            const v = counts[s.key] || 0;
            const pct = (v / total) * 100;
            if (pct <= 0) return null;
            return (
              <div
                key={s.key}
                title={`${s.label}: ${v}`}
                className={s.color}
                style={{ width: `${pct}%` }}
              />
            );
          })}
        </div>
        <div className="flex flex-wrap gap-3 text-[10px] uppercase tracking-wider text-zinc-500 mt-2">
          {STACK_KEYS.map((s) => (
            <div key={s.key} className="flex items-center gap-1.5">
              <span className={`inline-block h-2 w-3 ${s.color} rounded-sm`} />
              {s.label} = {counts[s.key] || 0}
            </div>
          ))}
        </div>
      </Section>

      {/* Errors */}
      {data.errors && data.errors.length > 0 && (
        <Section title={`Errors (${data.errors.length})`}>
          <div className="max-h-80 overflow-y-auto rounded-lg border border-rose-900/60 bg-rose-950/20 p-3 space-y-1 text-xs">
            {data.errors.map((e, i) => (
              <div key={i} className="text-rose-200 font-mono whitespace-pre-wrap">
                {e}
              </div>
            ))}
          </div>
        </Section>
      )}

      <div className="flex flex-wrap gap-3">
        <Link
          to={`/admin/failed-mappings?import_run_ulid=${data.ulid}`}
          className="text-sm text-emerald-300 hover:underline"
        >
          → linked failed_mappings
        </Link>
      </div>
    </div>
  );
}
